import React, { MouseEvent, useCallback, useMemo } from 'react';
import { GenericCard } from 'components/display/Card/Card';
import { Button } from 'components/ui/Button';
import { Sizes } from 'enums/sizes';
import { usePanel } from 'hooks/usePanel';
import { getContractStatusLabel } from '../../utils/getContractCardInfos';
import GenericBadge from 'components/ui/Badge';
import { GenericColors } from 'enums/genericColors';
import { CardState } from '../../types/cardState';
import { NsKey } from 'enums/NsKey';
import { useTranslation } from 'react-i18next';
import { TitleIconFactory } from 'factories/TitleIconFactory';
import { TitleIconKey } from 'enums/titleIconKey';
import { MandateSignatureRequest } from '../signature/MandateSignatureRequest';
import {
  useQuoteContractualizationData,
  useIsSendingContract
} from '../../store';
import { useSubscriptionProcessActions } from 'features/subscription-process/contexts/SubscriptionProcessContext';
import { useRequiresOwnMandate } from '../../hooks/useRequiresOwnMandate';
import { useCurrentAccess } from 'features/access/hooks/useCurrentAccess';
import { DeclareGerantPanel } from 'features/access/components/team/DeclareGerantPanel';
import {
  mapApiStatusToCardState,
  mapApiSignatureToContractStatus
} from '../../types/api';

/**
 * Paying firm: only a (legal) manager of the firm sends the mandate the firm pays for.
 * The back puts one of these reasons on `disabledReasons.canSignMandate` and describes
 * the firm in `cabinetMandate` — the card only displays them.
 * See dox core/mandates/back/cabinet-signatory.
 */
const CABINET_REASONS = {
  noGerant: 'MANDATE_NO_GERANT',
  gerantWithoutAccess: 'MANDATE_GERANT_WITHOUT_ACCESS',
  notGerant: 'MANDATE_NOT_GERANT'
} as const;

/**
 * Quote validation: while the validator's approval is missing (or refused), the back puts
 * one of these reasons on `disabledReasons.canSignMandate`, whether a firm pays or not.
 * Their texts are shared with the contract card (`SEND_CONTRACT_DISABLED`).
 */
const VALIDATION_REASONS = [
  'VALIDATION_REQUIRED',
  'VALIDATION_PENDING',
  'VALIDATION_REFUSED',
  'VALIDATION_CONFIG_INCOMPLETE'
] as const;

/** Reasons the card explains instead of the sending button. */
const MANDATE_REASONS: readonly string[] = [
  ...Object.values(CABINET_REASONS),
  ...VALIDATION_REASONS
];

const isValidationReason = (reason: string | null): boolean =>
  reason != null && (VALIDATION_REASONS as readonly string[]).includes(reason);

export const SignMandateCard: React.FC = () => {
  const { openPanel } = usePanel();
  const { t } = useTranslation(NsKey.Contractualization);

  const quoteData = useQuoteContractualizationData();
  const isSendingContract = useIsSendingContract();
  const { refreshSubscriptionProcess } = useSubscriptionProcessActions();
  const { access, status: accessStatus, refresh: refreshAccess } = useCurrentAccess();

  const cabinet = quoteData?.cabinetMandate ?? null;
  const canSignMandate = quoteData?.permissions?.canSignMandate === true;
  // A reason only explains a refusal: when the back permits signing, it must not hide
  // the sending button (the two flags are not coupled server-side).
  const mandateReason = useMemo(() => {
    const reason = quoteData?.disabledReasons?.canSignMandate;
    if (!reason || canSignMandate || !MANDATE_REASONS.includes(reason)) return null;
    // The firm reasons only make sense when a firm pays.
    return isValidationReason(reason) || cabinet ? reason : null;
  }, [cabinet, canSignMandate, quoteData?.disabledReasons?.canSignMandate]);
  const validationReason = isValidationReason(mandateReason) ? mandateReason : null;
  const cabinetReason = validationReason ? null : mandateReason;

  // The owner can declare the manager without leaving the file: the card is then not
  // "disabled", it offers the unblocking action. Ownership comes from the quote itself;
  // the current access (async) only feeds the panel once it has loaded.
  const canDeclareGerant =
    cabinetReason === CABINET_REASONS.noGerant && cabinet?.currentIsOwner === true;
  const requiresOwnMandate = useRequiresOwnMandate(
    quoteData?.quoteId,
    quoteData?.requiresOwnMandate
  );

  // La carte est visible seulement si cardStatuses.mandate existe (backend).
  //
  // S'y ajoute le cas de l'aliment dont le souscripteur du cadre règle pour tous : il n'a
  // pas de mandat propre à faire signer. Le back n'expose alors pas la carte, sauf pour
  // les aliments d'avant le garde-fou serveur, qui portent encore un mandat sur le compte
  // de l'assuré — proposer de l'envoyer en signature entérinerait le mauvais prélèvement.
  const isCardVisible = useMemo(() => {
    return requiresOwnMandate && quoteData?.cardStatuses?.mandate != null;
  }, [requiresOwnMandate, quoteData?.cardStatuses?.mandate]);

  // Statut de la carte depuis le backend
  const currentCardState = useMemo(() => {
    return mapApiStatusToCardState(quoteData?.cardStatuses?.mandate?.status);
  }, [quoteData?.cardStatuses?.mandate?.status]);

  // Statut de signature du mandat
  const currentStatus = useMemo(() => {
    return mapApiSignatureToContractStatus(quoteData?.mandateSignatureStatus);
  }, [quoteData?.mandateSignatureStatus]);

  // Desactivation basee sur les permissions du backend
  const isCardDisabled = useMemo(() => {
    if (isSendingContract) {
      return true;
    }
    if (canDeclareGerant) {
      return false;
    }
    return !canSignMandate;
  }, [isSendingContract, canDeclareGerant, canSignMandate]);

  const isSendMandateDisabled = useMemo(() => {
    return currentCardState !== CardState.IN_PROGRESS;
  }, [currentCardState]);

  const getBadgeColor = useMemo(() => {
    switch (currentCardState) {
      case CardState.CRITICAL:
        return GenericColors.Red;
      case CardState.IN_PROGRESS:
        return GenericColors.Yellow;
      case CardState.VALID:
        return GenericColors.Green;
      default:
        return GenericColors.Gray;
    }
  }, [currentCardState]);

  const onSelectMandateSignatureRequest = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      if (!quoteData) return;
      openPanel(
        {
          title: 'Envoyer le mandat pour signature',
          size: Sizes.xl
        },
        <MandateSignatureRequest onSuccess={refreshSubscriptionProcess} />
      );
    },
    [openPanel, quoteData, refreshSubscriptionProcess]
  );

  const onDeclareGerant = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      if (!access) return;
      openPanel(
        {
          title: t('CONTRACTUALIZATION.CARDS.MANDATE_DECLARE_GERANT_PANEL_TITLE'),
          size: Sizes.lg
        },
        <DeclareGerantPanel
          personneMoraleId={access.personneMoraleId}
          defaultAccessId={access.accessId}
          phoneRequired
          onSuccess={refreshSubscriptionProcess}
        />
      );
    },
    [access, openPanel, refreshSubscriptionProcess, t]
  );

  const cabinetName = cabinet?.cabinetName ?? '';
  const gerantName = cabinet?.gerantName ?? '';

  const renderCabinetBody = () => {
    switch (cabinetReason) {
      case CABINET_REASONS.noGerant:
        return (
          <div className="flex flex-col items-center gap-3 text-center text-sm">
            <p className="font-medium text-gray-800">
              {t('CONTRACTUALIZATION.CARDS.MANDATE_NO_GERANT', { cabinet: cabinetName })}
            </p>
            {canDeclareGerant ? (
              <>
                <p className="text-gray-600">
                  {t('CONTRACTUALIZATION.CARDS.MANDATE_NO_GERANT_OWNER_HINT')}
                </p>
                <Button
                  color="secondary"
                  round
                  onClick={onDeclareGerant}
                  disabled={isSendingContract || !access}
                >
                  {t('CONTRACTUALIZATION.CARDS.MANDATE_DECLARE_GERANT')}
                </Button>
                {accessStatus === 'error' && (
                  <button
                    type="button"
                    className="text-xs text-red-600 underline"
                    onClick={(e) => { e.stopPropagation(); refreshAccess(); }}
                  >
                    {t('CONTRACTUALIZATION.CARDS.MANDATE_ACCESS_LOAD_FAILED')}
                  </button>
                )}
              </>
            ) : (
              <p className="text-gray-600">
                {t('CONTRACTUALIZATION.CARDS.MANDATE_NO_GERANT_ASK_OWNER')}
              </p>
            )}
          </div>
        );
      case CABINET_REASONS.gerantWithoutAccess:
        return (
          <p className="text-center text-sm text-gray-700">
            {gerantName
              ? t('CONTRACTUALIZATION.CARDS.MANDATE_GERANT_WITHOUT_ACCESS', { gerant: gerantName })
              : t('CONTRACTUALIZATION.CARDS.MANDATE_GERANT_WITHOUT_ACCESS_NO_NAME')}
          </p>
        );
      case CABINET_REASONS.notGerant:
        return (
          <p className="text-center text-sm text-gray-600">
            {gerantName
              ? t('CONTRACTUALIZATION.CARDS.MANDATE_NOT_GERANT', { gerant: gerantName })
              : t('CONTRACTUALIZATION.CARDS.MANDATE_NOT_GERANT_NO_NAME')}
          </p>
        );
      default:
        return null;
    }
  };

  const renderBadge = useMemo(() => {
    return (
      <GenericBadge
        color={getBadgeColor}
        label={getContractStatusLabel(currentStatus)}
        badgeType="status"
      />
    );
  }, [getBadgeColor, currentStatus]);

  if (!isCardVisible) {
    return <></>;
  }

  return (
    <GenericCard className="w-full">
      <GenericCard.Header
        disabled={isCardDisabled || isSendMandateDisabled}
        icon={<TitleIconFactory titleIconKey={TitleIconKey.Mandate} />}
        titleAside={renderBadge}
      >
        {t('CONTRACTUALIZATION.CARDS.SIGN_MANDATE_TITLE')}
      </GenericCard.Header>
      {!isSendMandateDisabled && (
        <GenericCard.Body>
          {validationReason ? (
            <p className="text-center text-sm text-gray-600">
              {t(`CONTRACTUALIZATION.CARDS.SEND_CONTRACT_DISABLED.${validationReason}`)}
            </p>
          ) : cabinetReason ? (
            renderCabinetBody()
          ) : (
            <div className="flex flex-col items-center justify-center gap-3">
              {cabinet && (
                <p className="text-center text-xs text-gray-500">
                  {t('CONTRACTUALIZATION.CARDS.MANDATE_CABINET_PAYS', { cabinet: cabinetName })}
                </p>
              )}
              <Button
                color="secondary"
                round
                onClick={onSelectMandateSignatureRequest}
                disabled={isCardDisabled || isSendMandateDisabled}
              >
                {t('CONTRACTUALIZATION.CARDS.SIGN_BUTTON')}
              </Button>
            </div>
          )}
        </GenericCard.Body>
      )}
    </GenericCard>
  );
};
